import { v4 as uuidv4 } from "uuid";
import { getCreationStamp, getModificationStamp, initDb } from "../tools/conf";
import { generateTaxonomyList } from "../tools/dataConverter";
import { checkTaxonEntry } from "../utils/species/taxonEntryGuard";
import { catalogueName } from "../enrichment-tags-v2/core/enrichmentTagCatalogue";

// Identifiant de famille ou d'ordre fourni à la création (réservé par l'agent espèces).
const RESERVED_TAXON_ID = /^[A-Za-z0-9_-]{8,64}$/;

export class akongoGlobalDatabaseConnector {
  /**
   * Récupère les informations d'un tag par son ID
   * @param {string} tagId - ID du tag à récupérer
   * @returns {Promise<Object|null>} - Données du tag ou null si non trouvé
   */
  async _getTagById(tagId, lang = "fr") {
    try {
      if (!tagId) return null;

      const db = initDb();
      const tagDoc = await db.collection("tags").doc(tagId).get();

      if (tagDoc.exists) {
        const tagData = tagDoc.data();
        // Langue demandée, puis anglais, puis français (le nom saisi), décision du 03/11/2026
        const name = catalogueName(tagData, lang) || tagData.name;
        return {
          ...tagData,
          name,
        };
      }

      return null;
    } catch (error) {
      console.error("Erreur lors de la récupération du tag:", error);
      return null;
    }
  }
  /**
   * Get all available tags from the global database
   * @returns {Promise<Array>} - Array of tag objects
   */
  async _getTagsFromGlobalList({ throwOnError = false } = {}) {
    try {
      let db = initDb();
      let snapshot = await db
        .collection("tags")
        .where("archived", "==", false)
        .get();

      let tags = [];
      snapshot.forEach((doc) => {
        const tagData = doc.data();
        tags.push({
          tagId: tagData.id || doc.id,
          tagName:
            tagData.name ||
            tagData.tagName ||
            `Tag ${doc.id.substring(0, 4)}...`,
          ...tagData,
        });
      });

      return tags;
    } catch (error) {
      console.error("Error fetching global tags:", error);
      if (throwOnError) throw error;
      return [];
    }
  }

  async _getAkongoStaff() {
    let db = await initDb();
    let list = [];
    try {
      let snapshot = await db
        .collection("users")
        .where("akongoStaff", "==", true)
        .get();
      if (snapshot.empty) {
      } else {
        snapshot.forEach((doc) => {
          let data = doc.data();
          list.push({
            ...data,
            label: data.userFirstname + " " + data.userLastname,
            value: data.userId,
          });
        });
      }
      return list;
    } catch {
      console.log(console.error());
    }
  }

  async _getSpeciesList() {
    let db = await initDb();
    let list = [];
    try {
      let snapshot = await db
        .collection("species")
        .get();

      if (snapshot.empty) {
        console.log('No species found in collection');
      } else {
        snapshot.forEach((doc) => {
          let specieData = doc.data();
          list.push({
            label: specieData.specieName + " / " + specieData.specieLatinName,
            value: specieData.specieId,
            ...specieData,
          });
        });
      }

      // Une espèce sans nom commun (création incomplète) ne doit pas faire
      // échouer tout le catalogue : elle est triée sur son nom latin.
      const sortName = (item) =>
        String(item.specieName || item.specieLatinName || "")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");
      list.sort(function (a, b) {
        if (sortName(a) < sortName(b)) {
          return -1;
        }
        if (sortName(a) > sortName(b)) {
          return 1;
        }
        return 0;
      });

      return list;
    } catch {
      console.log(console.error());
    }
  }

  async _getTaxonomy(specieId) {
    let db = initDb();
    try {
      let snapshot = await db.collection("species").doc(specieId).get();
      return snapshot.data();
    } catch (error) {
      console.log(error);
    }
  }

  async _getTaxonomyFromLatin(latinName) {
    let db = initDb();
    try {
      let snapshot = await db
        .collection("species")
        .where("specieLatinName", "==", latinName)
        .get();
      let list = [];
      if (snapshot.empty) {
        return null;
      } else {
        snapshot.forEach((doc) => {
          list.push(doc.data());
        });
        return list[0];
      }
    } catch (error) {
      console.log(error);
    }
  }

  async _getTaxonomyList() {
    let remoteData = await Promise.all([
      await this._getSpeciesList(),
      await this._getOrderList(),
      await this._getFamilyList(),
    ]);

    let taxList = generateTaxonomyList(
      remoteData[0],
      remoteData[1],
      remoteData[2]
    );

    return taxList;
  }

  async _addSpecieInGlobalList(newSpecieData) {
    try {
      let db = await initDb();
      // Utiliser le specieId existant ou générer un nouveau UUID
      let uuid = newSpecieData.specieId || uuidv4();
      let familyId = null;
      let orderId = null;
      try {
        familyId = newSpecieData.specieFamily.familyId;
        orderId = newSpecieData.specieFamily.familyOrder.orderId;
      } catch (error) {
        console.log(error);
      }

      // L'import IUCN pose déjà son propre `createdAt` (Timestamp Firestore) et
      // un `creationSource` — on les laisse passer. L'auteur, lui, est toujours
      // repris de l'estampille : c'est le seul champ qu'on refuse de laisser au
      // payload, pour qu'aucun chemin d'appel ne puisse attribuer une création
      // à quelqu'un d'autre que l'utilisateur courant.
      const creationStamp = getCreationStamp();
      let newEntry = {
        ...creationStamp,
        ...newSpecieData,
        createdBy: creationStamp.createdBy,
        createdByName: creationStamp.createdByName,
        specieId: uuid,
        familyId: familyId,
        orderId: orderId,
      };

      console.log("newEntry");
      console.log(newEntry);

      await db
        .collection("species")
        .doc(uuid)
        .set({ ...newEntry, ...getModificationStamp() });
      return { success: true, data: newEntry };
    } catch (error) {
      console.log(error);
      return { success: false, data: error };
    }
  }

  async _modifySpecieFromGlobalList(newSpecieData) {
    let db = await initDb();
    // La famille choisie fait foi : familyId / orderId en sont dérivés et
    // priment sur ceux du document relu (sinon changer de famille gardait
    // l'ancienne). Sans champ specieFamily, les identifiants restent tels quels.
    const derivedTaxonomy = Object.prototype.hasOwnProperty.call(newSpecieData, "specieFamily")
      ? {
          familyId: newSpecieData.specieFamily?.familyId ?? null,
          orderId: newSpecieData.specieFamily?.familyOrder?.orderId ?? null,
        }
      : {};
    let newEntry = {
      ...newSpecieData,
      ...derivedTaxonomy,
    };

    try {
      // Écriture attendue : un refus (règles, réseau) doit remonter comme un
      // échec, pas comme un « enregistré ».
      await db.collection("species")
        .doc(newSpecieData.specieId)
        .update({ ...newEntry, ...getModificationStamp() });
      return { success: true, data: newEntry };
    } catch (error) {
      console.log(error);
      return { success: false, data: error };
    }
  }

  async _removeSpecieFromGobalList(specieId) {
    try {
      let db = await initDb();
      await db.collection("species").doc(specieId).delete();
      return { success: true };
    } catch (error) {
      return { success: false, data: error };
    }
  }

  // family

  async _getFamilyList() {
    let db = await initDb();
    let list = [];
    try {
      let snapshot = await db.collection("family").get();
      if (snapshot.empty) {
      } else {
        snapshot.forEach((doc) => {
          let familyData = doc.data();
          list.push({
            label: familyData.familyName + " - " + familyData.familyLatinName,
            value: familyData.familyId,
            ...familyData,
          });
        });
      }
      return list;
    } catch {
      console.log(console.error());
    }
  }

  // Écritures de familles et d'ordres : toutes passent par checkTaxonEntry
  // (noms français, anglais et latin exigés, espaces retirés, jamais deux
  // entrées du même nom latin). Une création qui retombe sur une entrée
  // existante la rend (`reused: true`) au lieu d'en créer une seconde ; une
  // modification qui créerait un doublon est refusée (`code: "DUPLICATE"`).
  // Une création peut porter son identifiant (réservé par l'agent espèces pour
  // les cartes qui créent le même taxon) : l'entrée est écrite sous cet
  // identifiant, ou rendue telle quelle si elle existe déjà.
  async _writeTaxonEntry(kind, data, { create }) {
    const collection = kind === "family" ? "family" : "order";
    const idField = kind === "family" ? "familyId" : "orderId";
    try {
      const db = await initDb();
      const snapshot = await db.collection(collection).get();
      const existing = snapshot.docs.map((doc) => ({ ...doc.data(), [idField]: doc.data()[idField] || doc.id }));
      const current = create ? null : existing.find((item) => item[idField] === data[idField]) || null;
      if (!create && !current) return { success: false, code: "NOT_FOUND", data: null };
      const wantedId = create && RESERVED_TAXON_ID.test(data[idField] || "") ? data[idField] : null;
      const reserved = wantedId && existing.find((item) => item[idField] === wantedId);
      if (reserved) return { success: true, reused: true, data: reserved };
      const check = checkTaxonEntry(kind, data, existing, current);
      if (!check.ok && check.code === "DUPLICATE" && create) return { success: true, reused: true, data: check.duplicate };
      if (!check.ok) return { success: false, code: check.code, data: check.duplicate || check.missing };
      if (create) {
        const id = wantedId || uuidv4();
        const entry = { ...check.entry, [idField]: id };
        await db.collection(collection).doc(id).set(entry);
        return { success: true, data: entry };
      }
      await db.collection(collection).doc(data[idField]).update(check.entry);
      return { success: true, data: { ...current, ...check.entry } };
    } catch (error) {
      return { success: false, data: error };
    }
  }

  async _addFamilyInGlobalList(newFamilyData) {
    return this._writeTaxonEntry("family", newFamilyData, { create: true });
  }

  async _modifyFamilyFromGlobalList(newFamilyData) {
    return this._writeTaxonEntry("family", newFamilyData, { create: false });
  }

  async _removeFamilyFromGobalList(familyId) {
    try {
      let db = await initDb();
      await db.collection("family").doc(familyId).delete();
      return { success: true };
    } catch (error) {
      return { success: false, data: error };
    }
  }

  // family

  async _getOrderList() {
    let db = await initDb();
    let list = [];
    try {
      let snapshot = await db.collection("order").get();
      if (snapshot.empty) {
      } else {
        snapshot.forEach((doc) => {
          let orderData = doc.data();

          list.push({
            label: orderData.orderName + " - " + orderData.orderLatinName,
            value: orderData.orderId,
            ...orderData,
          });
        });
      }
      return list;
    } catch {
      console.log(console.error());
    }
  }

  async _addOrderInGlobalList(newOrderData) {
    return this._writeTaxonEntry("order", newOrderData, { create: true });
  }

  async _modifyOrderFromGlobalList(newOrderData) {
    return this._writeTaxonEntry("order", newOrderData, { create: false });
  }

  async _removeOrderFromGobalList(orderId) {
    try {
      let db = await initDb();
      await db.collection("order").doc(orderId).delete();
      return { success: true };
    } catch (error) {
      return { success: false, data: error };
    }
  }

  async _getSurveyAmountFromSpecieId(specieId, allQuizz) {
    let amount = 0;
    let prod = 0;
    allQuizz.map((el) => {
      console.log(el);
      try {
        if (el.surveySpecie.specieId === specieId) {
          amount = amount + 1;
          prod = prod + el.activeCampaigns.length;
        }
      } catch (error) {}
    });

    return {
      amount: amount,
      prod: prod,
    };
  }

  async _getObsAmountFromSpecieId(specieId, allObs) {
    let amount = 0;
    allObs.map((el) => {
      console.log(el);
      try {
        el.obsSpecie.map((el) => {
          if (el.specieId === specieId) {
            amount = amount + 1;
          }
        });
      } catch (error) {}
    });

    return amount;
  }

  // Short management

  async _getShortQuestionsTable() {
    let db = initDb();
    let shortQuestionTable = (
      await db.collection("various").doc("shortQuestionTable").get()
    ).data();
    return shortQuestionTable;
  }

  // Version Management

  async _addEntryToSurveyLog(newEntry) {
    try {
      let db = initDb();
      let res = await db
        .collection("species")
        .doc(newEntry.specieId)
        .collection("logs")
        .doc(newEntry.timestamp.toString())
        .set(newEntry);
      return null;
    } catch (error) {
      return null;
    }
  }

  async _setSpecieUnderReview(specieId, reviewData) {
    try {
      let db = initDb();
      await db.collection("species").doc(specieId).update({
        underReview: true,
        reviewData: reviewData,
      });
      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
      };
    }
  }

  async _getSurveyLogs(specieId, limit) {
    try {
      let db = initDb();
      let snap = await db
        .collection("species")
        .doc(specieId)
        .collection("logs")
        .orderBy("timestamp", "desc")
        .limit(limit)
        .get();
      let data = [];
      snap.forEach((el) => {
        data.push(el.data());
      });
      return data;
    } catch (error) {
      console.log(error);
      return [];
    }
  }

  // Création
  async createCategory(newCategory) {
    try {
      let db = initDb();
      // On peut initialiser archived à false si besoin
      await db
        .collection("categories")
        .doc(newCategory.id)
        .set({ archived: false, ...newCategory });
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }

  async createTag(newTag) {
    try {
      let db = initDb();
      await db
        .collection("tags")
        .doc(newTag.id)
        .set({ archived: false, ...newTag });
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }

  async createGoal(newGoal) {
    try {
      let db = initDb();
      await db
        .collection("goals")
        .doc(newGoal.id)
        .set({ archived: false, ...newGoal });
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }

  // Mise à jour
  async updateCategory(categoryId, categoryData) {
    try {
      let db = initDb();
      await db
        .collection("categories")
        .doc(categoryId)
        .update({ ...categoryData });
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }

  async updateTag(tagId, tagData) {
    try {
      let db = initDb();
      await db
        .collection("tags")
        .doc(tagId)
        .update({ ...tagData });
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }

  async updateGoal(goalId, goalData) {
    try {
      let db = initDb();
      await db
        .collection("goals")
        .doc(goalId)
        .update({ ...goalData });
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }

  // Archivage (anciennement suppression)
  async deleteCategory(categoryId) {
    try {
      let db = initDb();
      await db
        .collection("categories")
        .doc(categoryId)
        .update({ archived: true });
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }

  async deleteTag(tagId) {
    try {
      let db = initDb();
      await db.collection("tags").doc(tagId).update({ archived: true });
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }

  async deleteGoal(goalId) {
    try {
      let db = initDb();
      await db.collection("goals").doc(goalId).update({ archived: true });
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }

  // Récupération en filtrant les documents archivés
  async getCategories() {
    try {
      let db = initDb();
      // Sélectionne les catégories non archivées (archived false ou non défini)
      let snap = await db
        .collection("categories")
        .where("archived", "==", false)
        .get();
      let data = [];
      snap.forEach((doc) => data.push(doc.data()));
      return data;
    } catch (error) {
      console.log(error);
      return [];
    }
  }

  async getTags() {
    try {
      let db = initDb();
      let snap = await db
        .collection("tags")
        .where("archived", "==", false)
        .get();
      let data = [];
      snap.forEach((doc) => data.push(doc.data()));
      return data;
    } catch (error) {
      console.log(error);
      return [];
    }
  }

  /**
   * Objectifs d'enrichissement du catalogue global. Par défaut les seuls
   * objectifs actifs ; `includeArchived` les rend tous, pour nommer les
   * objectifs archivés que d'anciennes évaluations citent encore (analyse).
   */
  async getGoals({ includeArchived = false } = {}) {
    try {
      let db = initDb();
      const collection = db.collection("goals");
      let snap = await (includeArchived ? collection : collection.where("archived", "==", false)).get();
      let data = [];
      snap.forEach((doc) => {
        const goalData = doc.data();
        // `label` reste le nom français saisi (relu et réenregistré par
        // TagManagementView) : l'affichage passe par `catalogueName(goal, langue)`.
        data.push({
          ...goalData,
          label: goalData.trad ? goalData.trad.fr : goalData.label,
        });
      });
      return data;
    } catch (error) {
      console.log(error);
      return [];
    }
  }

  async getGoalById(goalId, lang = "fr") {
    try {
      let db = initDb();
      const doc = await db.collection("goals").doc(goalId).get();
      if (!doc.exists) {
        return null;
      }
      const goalData = doc.data();
      // Langue demandée, puis anglais, puis français (le libellé saisi), décision du 03/11/2026
      const label = catalogueName(goalData, lang) || goalData.label;
      return {
        ...goalData,
        label,
      };
    } catch (error) {
      console.error("Error fetching goal:", error);
      return null;
    }
  }
}
